<!--
  @component AuditList
  @description Bandeja de casos de auditoría con filtros, asignación inline y paginación.
-->
<template>
  <div class="audit-list">
    <div class="audit-list__toolbar">
      <el-input
        v-model="searchInput"
        placeholder="Buscar por afiliado o prestación..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        style="width: 280px"
        @input="onSearch"
      />
      <el-select
        v-model="filterStatus"
        placeholder="Todos los estados"
        clearable
        size="small"
        style="width: 160px"
        @change="loadCases"
      >
        <el-option label="Pendiente" value="pending" />
        <el-option label="En revisión" value="under_review" />
        <el-option label="Aprobado" value="approved" />
        <el-option label="Rechazado" value="rejected" />
      </el-select>
      <el-select
        v-model="filterAuditor"
        placeholder="Todos los auditores"
        clearable
        size="small"
        style="width: 180px"
        @change="loadCases"
      >
        <el-option
          v-for="a in auditors"
          :key="a._id"
          :label="`${a.firstName} ${a.lastName}`"
          :value="a._id"
        />
      </el-select>
    </div>

    <div class="audit-list__card">
      <el-table
        v-loading="isLoading"
        :data="cases"
        size="small"
        border
        :row-style="{ cursor: 'pointer' }"
        style="width: 100%"
        @row-click="goToDetail"
      >
        <el-table-column label="N° Auth" width="80">
          <template slot-scope="{ row }">
            <span class="audit-list__case-num">#{{ row.authCaseNumber }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Afiliado" min-width="160">
          <template slot-scope="{ row }">
            <div class="audit-list__member-name">
              {{ row.memberSnapshot.lastName }},
              {{ row.memberSnapshot.firstName }}
            </div>
            <div class="audit-list__member-meta">
              {{ row.memberSnapshot.planName || "Sin plan" }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Prestación"
          min-width="200"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span class="audit-list__practice-code">{{
              row.practiceSnapshot.code
            }}</span>
            <span class="audit-list__practice-name">{{
              row.practiceSnapshot.name
            }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Categoría" width="120">
          <template slot-scope="{ row }">
            <span class="audit-list__badge audit-list__badge--category">
              {{ categoryLabel(row.practiceSnapshot.category) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Especialidad" width="130">
          <template slot-scope="{ row }">
            <span v-if="row.specialtyName" class="audit-list__specialty">{{
              row.specialtyName
            }}</span>
            <span v-else class="audit-list__empty">—</span>
          </template>
        </el-table-column>

        <el-table-column label="Asignado a" width="160">
          <template slot-scope="{ row }">
            <span v-if="row.assignedToName" class="audit-list__assigned">{{
              row.assignedToName
            }}</span>
            <el-button
              v-else
              size="mini"
              type="text"
              @click.stop="openAssign(row)"
              >Asignar</el-button
            >
          </template>
        </el-table-column>

        <el-table-column label="Estado" width="120">
          <template slot-scope="{ row }">
            <span class="audit-list__badge" :class="statusClass(row.status)">
              {{ statusLabel(row.status) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Fecha" width="130">
          <template slot-scope="{ row }">
            <span class="audit-list__date">{{
              row.createdAt | formatDate
            }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="audit-list__pagination">
        <el-pagination
          :current-page="currentPage"
          :page-size="limit"
          :total="total"
          layout="total, prev, pager, next"
          small
          @current-change="onPageChange"
        />
      </div>
    </div>

    <!-- Dialog asignación -->
    <el-dialog
      title="Asignar auditor"
      :visible.sync="assignDialog"
      width="400px"
    >
      <el-select
        v-model="assignAuditorId"
        placeholder="Seleccionar auditor"
        style="width: 100%"
      >
        <el-option
          v-for="a in auditors"
          :key="a._id"
          :label="`${a.firstName} ${a.lastName} (${roleLabel(a.type)})`"
          :value="a._id"
        />
      </el-select>
      <span slot="footer">
        <el-button size="small" @click="assignDialog = false"
          >Cancelar</el-button
        >
        <el-button
          size="small"
          type="primary"
          :loading="assigning"
          @click="confirmAssign"
          >Asignar</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import debounce from "debounce";
import auditService from "@/services/audit";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component AuditList */
export default {
  name: "AuditList",

  filters: {
    /**
     * @description Formatea fecha ISO a dd/MM/yyyy HH:mm
     * @param {string} value
     * @returns {string}
     */
    formatDate(value) {
      if (!value) return "-";
      return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: es });
    },
  },

  data() {
    return {
      cases: [],
      auditors: [],
      isLoading: false,
      total: 0,
      currentPage: 1,
      limit: 20,
      searchInput: "",
      filterStatus: "",
      filterAuditor: "",
      assignDialog: false,
      assignAuditorId: "",
      assigningCase: null,
      assigning: false,
    };
  },

  created() {
    this.onSearch = debounce(this.loadCases, 350);
    this.loadAuditors();
    this.loadCases();
  },

  methods: {
    /** @description Carga casos de auditoría con filtros activos */
    async loadCases() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.limit,
          limit: this.limit,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterAuditor) params.assignedTo = this.filterAuditor;
        const result = await auditService.get(params);
        this.cases = result.docs;
        this.total = result.total;
      } catch (e) {
        this.$message.error("Error al cargar los casos");
      } finally {
        this.isLoading = false;
      }
    },

    /** @description Carga la lista de auditores disponibles */
    async loadAuditors() {
      try {
        this.auditors = await auditService.getAuditors();
      } catch (e) {
        /* silencioso */
      }
    },

    /** @param {Object} row */
    goToDetail(row) {
      this.$router.push({ name: "audit-detail", params: { id: row._id } });
    },

    /** @param {number} page */
    onPageChange(page) {
      this.currentPage = page;
      this.loadCases();
    },

    /** @param {Object} row */
    openAssign(row) {
      this.assigningCase = row;
      this.assignAuditorId = row.assignedTo?._id || "";
      this.assignDialog = true;
    },

    /** @description Confirma asignación de auditor al caso */
    async confirmAssign() {
      if (!this.assignAuditorId) return;
      this.assigning = true;
      try {
        await auditService.assign(this.assigningCase._id, this.assignAuditorId);
        this.$message.success("Auditor asignado");
        this.assignDialog = false;
        this.loadCases();
      } catch (e) {
        this.$message.error("Error al asignar");
      } finally {
        this.assigning = false;
      }
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusClass(status) {
      return (
        {
          pending: "audit-list__badge--pending",
          under_review: "audit-list__badge--review",
          approved: "audit-list__badge--approved",
          rejected: "audit-list__badge--rejected",
        }[status] || ""
      );
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusLabel(status) {
      return (
        {
          pending: "Pendiente",
          under_review: "En revisión",
          approved: "Aprobado",
          rejected: "Rechazado",
        }[status] || status
      );
    },

    /**
     * @param {string} category
     * @returns {string}
     */
    categoryLabel(category) {
      return (
        {
          consultation: "Consulta",
          lab: "Laboratorio",
          imaging: "Imágenes",
          surgery: "Cirugía",
          dental: "Odontología",
          mental_health: "Salud mental",
          rehabilitation: "Rehabilitación",
          emergency: "Urgencias",
          hospitalization: "Internación",
          maternity: "Maternidad",
          medication: "Medicación",
          supply: "Insumos",
          prosthesis: "Prótesis",
          other: "Otros",
        }[category] ||
        category ||
        "-"
      );
    },

    /**
     * @param {string} type
     * @returns {string}
     */
    roleLabel(type) {
      return type === "medical_auditor" ? "Médico auditor" : "Auditor admin";
    },
  },
};
</script>

<style scoped>
.audit-list {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.audit-list__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.audit-list__card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.audit-list__case-num {
  font-weight: 700;
  color: #002d5e;
  font-size: 13px;
}

.audit-list__member-name {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

.audit-list__member-meta {
  font-size: 11px;
  color: #05e5ba;
  font-weight: 600;
  margin-top: 1px;
}

.audit-list__practice-code {
  font-weight: 700;
  color: #05e5ba;
  font-size: 12px;
  margin-right: 4px;
}

.audit-list__practice-name {
  color: #1a1f36;
  font-size: 13px;
}

.audit-list__specialty {
  font-size: 12px;
  color: #6b7294;
}

.audit-list__assigned {
  font-size: 12px;
  color: #1a1f36;
  font-weight: 600;
}

.audit-list__empty {
  color: #cbd5e1;
  font-size: 13px;
}

.audit-list__date {
  font-size: 12px;
  color: #6b7294;
  white-space: nowrap;
}

.audit-list__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.audit-list__badge--category {
  background: #eef1f6;
  color: #002d5e;
}

.audit-list__badge--pending {
  background: #faeeda;
  color: #854f0b;
}

.audit-list__badge--review {
  background: #e6f1fb;
  color: #0c447c;
}

.audit-list__badge--approved {
  background: #eaf3de;
  color: #3b6d11;
}

.audit-list__badge--rejected {
  background: #fcebeb;
  color: #a32d2d;
}

.audit-list__pagination {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f2f7;
}
</style>
